// entityFace.ts — Face classification for panel entities
// Determines which panel face (front/back/both) each entity belongs to,
// used for 3D z-positioning and 2D face-filter visibility.

import type { PanelData } from '../types/panelvar';

export type EntityFace = 'front' | 'back' | 'both';

// Groups that are always on the front (exterior/form) face
const FRONT_GROUPS = new Set(['fhvar', 'fvvar']);

// Groups that are always on the back (interior) face
const BACK_GROUPS = new Set(['bpvar', 'ppvar']);

/** Parse entity group key from an entityId like 'fhvar-2' → 'fhvar' */
function parseGroup(entityId: string): string {
  const dash = entityId.lastIndexOf('-');
  return dash > 0 ? entityId.slice(0, dash) : entityId;
}

/** Parse slot index from an entityId like 'fhvar-2' → 2 */
function parseIndex(entityId: string): number {
  const dash = entityId.lastIndexOf('-');
  return dash > 0 ? parseInt(entityId.slice(dash + 1), 10) : -1;
}

/**
 * Determine which panel face an entity belongs to.
 * - fhvar/fvvar → 'front' (exterior feature strips)
 * - bpvar/ppvar → 'back' (interior brace/lift inserts)
 * - wcvar → reads the slot's explicit `face` field
 * - Everything else → 'both' (through-panel or neutral)
 */
export function getEntityFace(entityId: string, panel: PanelData): EntityFace {
  const group = parseGroup(entityId);

  if (FRONT_GROUPS.has(group)) return 'front';
  if (BACK_GROUPS.has(group)) return 'back';

  if (group === 'wcvar') {
    const idx = parseIndex(entityId);
    const slot = panel.wcvar[idx];
    if (slot?.enabled) {
      const f = slot.data.face;
      if (f === 'front') return 'front';
      if (f === 'back') return 'back';
    }
    return 'both'; // edge faces (left/right/top/bottom) or unknown
  }

  return 'both';
}

/** Check if an entity should be visible given the active face filter. */
export function isVisibleForFilter(entityFace: EntityFace, filter: EntityFace): boolean {
  if (filter === 'both') return true;
  if (entityFace === 'both') return true;
  return entityFace === filter;
}
